import {
  Alert,
  Badge,
  Button,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Settings } from 'lucide-react';
import { type JSX, useState } from 'react';

import { INVOICE_TYPES, type InvoiceType } from '../../invoices/api';
import {
  createFiscalPointOfSale,
  listFiscalPointsOfSale,
  updateFiscalPointOfSale,
} from '../../invoices/fiscal-points-of-sale-api';

const TYPE_LABELS: Record<InvoiceType, string> = {
  FACTURA_A: 'Factura A',
  FACTURA_B: 'Factura B',
  FACTURA_C: 'Factura C',
};

/**
 * `billing-module/design.md` §9.7. Reachable only from a sidebar entry
 * gated on `fiscal.config` (`layout/navigation.ts`): every user who lands
 * here already has the permission every request needs, same criterion the
 * rest of `apps/web-admin` applies (routes aren't individually guarded,
 * `AppShell`'s auth check plus a hidden nav entry is the established
 * pattern).
 */
export function FiscalPointOfSaleConfigPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [description, setDescription] = useState('');
  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceType[]>([]);

  const query = useQuery({ queryKey: ['fiscal-points-of-sale'], queryFn: listFiscalPointsOfSale });

  function resetForm(): void {
    setNumber('');
    setDescription('');
    setInvoiceTypes([]);
  }

  const createMutation = useMutation({
    mutationFn: () =>
      createFiscalPointOfSale({ number: Number(number), description, invoiceTypes }),
    onSuccess: async () => {
      toast('success', 'Guardado');
      setCreateModalOpen(false);
      resetForm();
      await queryClient.invalidateQueries({ queryKey: ['fiscal-points-of-sale'] });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      updateFiscalPointOfSale(id, { isActive }),
    onSuccess: async () => {
      toast('success', 'Guardado');
      await queryClient.invalidateQueries({ queryKey: ['fiscal-points-of-sale'] });
    },
  });

  function toggleInvoiceType(type: InvoiceType): void {
    setInvoiceTypes((current) =>
      current.includes(type) ? current.filter((value) => value !== type) : [...current, type],
    );
  }

  const points = query.data ?? [];

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Settings}
        title="PV fiscal (ARCA)"
        description="Configurá los PV fiscales habilitados para facturación electrónica."
        actions={
          <Button icon={Plus} onClick={() => setCreateModalOpen(true)}>
            Nuevo punto de venta
          </Button>
        }
      />

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar los puntos de venta">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined ? (
        <Table>
          <caption className="sr-only">PV fiscal (ARCA)</caption>
          <thead>
            <tr>
              <TableHeader>Número</TableHeader>
              <TableHeader>Descripción</TableHeader>
              <TableHeader>Tipos habilitados</TableHeader>
              <TableHeader>Último número por tipo</TableHeader>
              <TableHeader>Punto de venta</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <TableBody>
            {points.map((point) => (
              <TableRow key={point.id}>
                <TableCell>{point.number}</TableCell>
                <TableCell>{point.description}</TableCell>
                <TableCell>
                  {point.invoiceTypes.map((type) => TYPE_LABELS[type]).join(', ')}
                </TableCell>
                <TableCell>
                  {Object.entries(point.lastNumberByType)
                    .map(
                      ([type, lastNumber]) =>
                        `${TYPE_LABELS[type as InvoiceType] ?? type}: ${lastNumber}`,
                    )
                    .join(', ') || '—'}
                </TableCell>
                <TableCell>{point.operatingUnit?.name ?? 'Sin punto de venta'}</TableCell>
                <TableCell>
                  <Badge variant={point.isActive ? 'success' : 'neutral'}>
                    {point.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Button
                    variant="secondary"
                    size="sm"
                    loading={
                      toggleActiveMutation.isPending &&
                      toggleActiveMutation.variables?.id === point.id
                    }
                    onClick={() =>
                      toggleActiveMutation.mutate({ id: point.id, isActive: !point.isActive })
                    }
                  >
                    {point.isActive ? 'Desactivar' : 'Activar'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}

      {createModalOpen ? (
        <Modal
          open
          onClose={() => {
            setCreateModalOpen(false);
            resetForm();
          }}
          title="Nuevo punto de venta"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {createMutation.isError ? (
              <Alert tone="danger">
                No pudimos crear el punto de venta. Verificá los datos e intentá de nuevo.
              </Alert>
            ) : null}

            <Field
              label="Número"
              name="number"
              type="number"
              required
              value={number}
              onChange={(event) => setNumber(event.target.value)}
            />
            <Field
              label="Descripción"
              name="description"
              required
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />

            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium text-slate-900">Tipos de comprobante</legend>
              {INVOICE_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={invoiceTypes.includes(type)}
                    onChange={() => toggleInvoiceType(type)}
                  />
                  {TYPE_LABELS[type]}
                </label>
              ))}
            </fieldset>

            <div className="flex items-center gap-3">
              <Button
                loading={createMutation.isPending}
                disabled={
                  number.trim() === '' || description.trim() === '' || invoiceTypes.length === 0
                }
                onClick={() => createMutation.mutate()}
              >
                Confirmar
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setCreateModalOpen(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
